import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { TrendingUp, TrendingDown, DollarSign, Activity, ArrowUpRight, ArrowDownRight, Calendar } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { format, startOfWeek, endOfWeek } from 'date-fns';
import { fr } from 'date-fns/locale';
import { usePharmacies } from '../context/PharmacyContext';
import SimpleDateRangePicker from '../components/SimpleDateRangePicker';
import ModernDateRangePicker from '../components/ModernDateRangePicker';
import SuperAdminPharmacyFilter from '../components/SuperAdminPharmacyFilter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : BACKEND_URL.endsWith('/') ? `${BACKEND_URL}api` : `${BACKEND_URL}/api`;

function Dashboard() {
  const { getPharmacyIdsParam, selectedPharmacyIds } = usePharmacies();
  const [summary, setSummary] = useState(null);
  const [trends, setTrends] = useState([]);
  const [revenueCategories, setRevenueCategories] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUser] = useState(() => JSON.parse(sessionStorage.getItem('pharmacy_user') || localStorage.getItem('pharmacy_user') || '{}'));
  const [superAdminPharmacyFilter, setSuperAdminPharmacyFilter] = useState('all');
  const [dateRange, setDateRange] = useState(() => {
    const now = new Date();
    const day = now.getDay();
    const diff = day === 6 ? 0 : day + 1;
    const satStart = new Date(now);
    satStart.setDate(now.getDate() - diff);
    satStart.setHours(0, 0, 0, 0);
    const friEnd = new Date(satStart);
    friEnd.setDate(satStart.getDate() + 6);
    friEnd.setHours(23, 59, 59, 999);
    return { from: satStart, to: friEnd };
  });

  useEffect(() => {
    fetchDashboardData();
  }, [dateRange, selectedPharmacyIds, superAdminPharmacyFilter]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      if (!dateRange?.from) return;
      
      const start = format(dateRange.from, "yyyy-MM-dd'T'00:00:00");
      const end = format(dateRange.to || dateRange.from, "yyyy-MM-dd'T'23:59:59");
      
      const headers = { 'X-User-Id': currentUser?.id || '' };
      
      // For super admin, use the pharmacy filter directly
      let pharmacyIds;
      if (currentUser?.role === 'super_admin') {
        // Super admin: use the filter value directly
        pharmacyIds = superAdminPharmacyFilter === 'all' ? 'all' : superAdminPharmacyFilter;
      } else {
        // Regular admin: use pharmacy context
        pharmacyIds = getPharmacyIdsParam();
      }

      const [summaryRes, trendsRes, revCatRes, expCatRes] = await Promise.all([
        axios.get(`${API}/analytics/summary`, { params: { start_date: start, end_date: end, pharmacy_ids: pharmacyIds }, headers }),
        axios.get(`${API}/analytics/trends`, { params: { start_date: start, end_date: end, period: 'day', pharmacy_ids: pharmacyIds }, headers }),
        axios.get(`${API}/analytics/by-category`, { params: { start_date: start, end_date: end, type: 'revenue', pharmacy_ids: pharmacyIds }, headers }),
        axios.get(`${API}/analytics/by-category`, { params: { start_date: start, end_date: end, type: 'expense', pharmacy_ids: pharmacyIds }, headers }),
      ]);

      setSummary(summaryRes.data);
      setTrends(trendsRes.data);
      setRevenueCategories(revCatRes.data);
      setExpenseCategories(expCatRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-DZ', { 
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value) + ' DA';
  };

  const formatCompact = (value) => {
    if (value >= 1000000) {
      return (value / 1000000).toFixed(1) + 'M';
    } else if (value >= 1000) {
      return (value / 1000).toFixed(0) + 'K';
    }
    return value;
  };

  const calculateTrend = (data) => {
    if (!data || data.length < 2) return { percentage: 0, isPositive: true };
    
    const recent = data.slice(-7);
    const previous = data.slice(-14, -7);
    
    if (previous.length === 0) return { percentage: 0, isPositive: true };
    
    const recentAvg = recent.reduce((sum, d) => sum + (d.revenues || 0), 0) / recent.length;
    const previousAvg = previous.reduce((sum, d) => sum + (d.revenues || 0), 0) / previous.length;
    
    const change = previousAvg > 0 ? ((recentAvg - previousAvg) / previousAvg) * 100 : 0;
    
    return {
      percentage: Math.abs(change).toFixed(1),
      isPositive: change >= 0
    };
  };

  // Boehringer Ingelheim Brand Colors
  const COLORS = ['#00ff7f', '#80D2EB', '#FFDA60', '#B5B3E5', '#F5876A', '#004d3d'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="dashboard-page">
      {/* Super Admin Pharmacy Filter */}
      {currentUser?.role === 'super_admin' && (
        <SuperAdminPharmacyFilter 
          value={superAdminPharmacyFilter}
          onChange={setSuperAdminPharmacyFilter}
          currentUser={currentUser}
        />
      )}

      {/* Period Selector */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Vue d'ensemble</h1>
          <p className="text-sm text-gray-500">
            {dateRange?.from && format(dateRange.from, 'dd MMM yyyy', { locale: fr })} 
            {dateRange?.to && ` - ${format(dateRange.to, 'dd MMM yyyy', { locale: fr })}`}
          </p>
        </div>
        <ModernDateRangePicker 
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
        />
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-6">
        <Card className="stat-card border-l-4 border-l-[#00ff7f] hover:shadow-lg transition-shadow" data-testid="revenue-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Revenus Totaux</CardTitle>
            <div className="p-2 bg-green-50 rounded-lg">
              <TrendingUp className="w-5 h-5 text-[#00ff7f]" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-[#004d3d]" data-testid="total-revenue">
              {formatCurrency(summary?.total_revenues || 0)}
            </div>
            <div className="flex items-center justify-between mt-3">
              <p className="text-xs text-gray-500 flex items-center">
                <ArrowUpRight className="w-3 h-3 mr-1" />
                {summary?.revenues_count || 0} transactions
              </p>
              {(() => {
                const trend = calculateTrend(trends);
                return (
                  <span className={`text-xs font-semibold flex items-center px-2 py-1 rounded-full ${
                    trend.isPositive ? 'bg-green-100 text-[#004d3d]' : 'bg-red-100 text-red-700'
                  }`}>
                    {trend.isPositive ? '↑' : '↓'} {trend.percentage}%
                  </span>
                );
              })()}
            </div>
            {/* Mini Sparkline */}
            {trends.length > 0 && (
              <div className="mt-3 h-10">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trends.slice(-7)}>
                    <Line 
                      type="monotone" 
                      dataKey="revenues" 
                      stroke="#00ff7f"
                      strokeWidth={2} 
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="stat-card border-l-4 border-l-[#F5876A] hover:shadow-lg transition-shadow" data-testid="expense-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Dépenses Totales</CardTitle>
            <div className="p-2 bg-red-50 rounded-lg">
              <TrendingDown className="w-5 h-5 text-[#F5876A]" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-[#F5876A]" data-testid="total-expense">
              {formatCurrency(summary?.total_expenses || 0)}
            </div>
            <div className="flex items-center justify-between mt-3">
              <p className="text-xs text-gray-500 flex items-center">
                <ArrowDownRight className="w-3 h-3 mr-1" />
                {summary?.expenses_count || 0} transactions
              </p>
              <span className="text-xs font-semibold px-2 py-1 rounded-full bg-gray-100 text-gray-600">
                {formatCompact(summary?.total_expenses || 0)} DA
              </span>
            </div>
            {/* Mini Sparkline */}
            {trends.length > 0 && (
              <div className="mt-3 h-10">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trends.slice(-7)}>
                    <Line 
                      type="monotone" 
                      dataKey="expenses" 
                      stroke="#F5876A"
                      strokeWidth={2} 
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="stat-card border-l-4 border-l-[#80D2EB] hover:shadow-lg transition-shadow" data-testid="balance-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Bénéfice Net</CardTitle>
            <div className={`p-2 rounded-lg ${summary?.balance >= 0 ? 'bg-blue-50' : 'bg-red-50'}`}>
              <DollarSign className={`w-5 h-5 ${summary?.balance >= 0 ? 'text-[#80D2EB]' : 'text-[#F5876A]'}`} />
            </div>
          </CardHeader>
          <CardContent>
            <div className={`text-3xl font-bold ${summary?.balance >= 0 ? 'text-[#004d3d]' : 'text-[#F5876A]'}`} data-testid="balance">
              {formatCurrency(summary?.balance || 0)}
            </div>
            <div className="flex items-center justify-between mt-3">
              <p className="text-xs text-gray-500">
                {summary?.balance >= 0 ? 'Bénéfice' : 'Déficit'}
              </p>
              {summary?.total_revenues > 0 && (
                <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                  summary?.balance >= 0 ? 'bg-blue-100 text-[#004d3d]' : 'bg-red-100 text-[#F5876A]'
                }`}>
                  {((summary.balance / summary.total_revenues) * 100).toFixed(1)}% marge
                </span>
              )}
            </div>
            {/* Mini Sparkline */}
            {trends.length > 0 && (
              <div className="mt-3 h-10">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trends.slice(-7)}>
                    <Line 
                      type="monotone" 
                      dataKey="balance" 
                      stroke={summary?.balance >= 0 ? '#80D2EB' : '#F5876A'}
                      strokeWidth={2} 
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="stat-card border-l-4 border-l-[#B5B3E5] hover:shadow-lg transition-shadow" data-testid="activity-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Transactions</CardTitle>
            <div className="p-2 bg-purple-50 rounded-lg">
              <Activity className="w-5 h-5 text-[#B5B3E5]" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-[#004d3d]" data-testid="total-transactions">
              {(summary?.revenues_count || 0) + (summary?.expenses_count || 0)}
            </div>
            <div className="flex items-center justify-between mt-3">
              <p className="text-xs text-gray-500">ce mois</p>
              <div className="flex gap-2">
                <span className="text-xs font-semibold px-2 py-1 rounded-full bg-green-100 text-[#004d3d]">
                  {summary?.revenues_count || 0} ↑
                </span>
                <span className="text-xs font-semibold px-2 py-1 rounded-full bg-red-100 text-[#F5876A]">
                  {summary?.expenses_count || 0} ↓
                </span>
              </div>
            </div>
            {/* Transaction ratio bar */}
            <div className="mt-3 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-[#00ff7f] to-[#B5B3E5] transition-all"
                style={{ 
                  width: `${summary?.revenues_count > 0 ? (summary.revenues_count / ((summary?.revenues_count || 0) + (summary?.expenses_count || 0))) * 100 : 0}%` 
                }}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Trends Chart */}
      <Card data-testid="trends-chart">
        <CardHeader>
          <CardTitle>Tendances Financières</CardTitle>
          <CardDescription>Évolution des revenus et dépenses</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={trends}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="period" />
              <YAxis />
              <Tooltip formatter={(value) => formatCurrency(value)} />
              <Legend />
              <Line type="monotone" dataKey="revenues" stroke="#10b981" strokeWidth={2} name="Revenus" />
              <Line type="monotone" dataKey="expenses" stroke="#ef4444" strokeWidth={2} name="Dépenses" />
              <Line type="monotone" dataKey="balance" stroke="#0ea5e9" strokeWidth={2} name="Solde" />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Category Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card data-testid="revenue-categories-chart">
          <CardHeader>
            <CardTitle>Répartition des Revenus</CardTitle>
            <CardDescription>Par catégorie</CardDescription>
          </CardHeader>
          <CardContent>
            {revenueCategories.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={revenueCategories}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {revenueCategories.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-64 text-gray-500">
                Aucune donnée disponible
              </div>
            )}
          </CardContent>
        </Card>

        <Card data-testid="expense-categories-chart">
          <CardHeader>
            <CardTitle>Répartition des Dépenses</CardTitle>
            <CardDescription>Par catégorie</CardDescription>
          </CardHeader>
          <CardContent>
            {expenseCategories.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={expenseCategories}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                  <YAxis />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Bar dataKey="value" fill="#ef4444" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-64 text-gray-500">
                Aucune donnée disponible
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default Dashboard;
